import { useState } from "react";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const SERVICES = [
  { id: "oil_change", label: "Oil change", price: "from $89" },
  { id: "brakes", label: "Brake service", price: "from $249" },
  { id: "diagnostics", label: "Diagnostics", price: "from $149" },
  { id: "inspection", label: "State inspection", price: "$45" },
  { id: "tires", label: "Tire service", price: "from $129" },
  { id: "transmission", label: "Transmission", price: "from $399" },
  { id: "engine", label: "Engine repair", price: "estimate" },
  { id: "battery", label: "Battery replace", price: "from $189" },
];

export default function ServicePage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", vehicle_info: "",
    service_type: "oil_change", appointment_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    notes: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/service-appointments", { ...form, status: "scheduled" });
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  if (submitted) return (
    <PublicShell><section className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
      <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
      <h1 className="font-heading text-4xl mb-3 chrome-text inline-block">See you soon, {form.customer_name.split(" ")[0]}.</h1>
      <p className="text-slate-300">Your appointment is booked. We'll send a confirmation email shortly.</p>
    </section></PublicShell>
  );
  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Service department</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3"><span className="chrome-text">Certified service.</span> <span className="gold-text">Concierge care.</span></h1>
          <p className="text-slate-300">ASE-certified technicians, OEM-spec parts, and complimentary loaner vehicles. Book in 30 seconds — most appointments scheduled same-week.</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-2">
          <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>Services we offer</div>
          {SERVICES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setForm({ ...form, service_type: s.id })}
              className={`w-full text-left surface p-3 rounded-sm flex items-center justify-between hover:border-[#D4AF37]/50 transition-colors ${form.service_type === s.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : ""}`}
              data-testid={`service-option-${s.id}`}
            >
              <span className="text-sm">{s.label}</span>
              <span className="text-xs text-slate-500">{s.price}</span>
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="lg:col-span-3 surface p-8 rounded-md space-y-4" data-testid="service-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="text-sm">Your name *</Label><Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" data-testid="svc-name-input" /></div>
            <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
            <div><Label className="text-sm">Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
            <div><Label className="text-sm">Vehicle</Label><Input placeholder="2021 Honda Civic" value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
            <div className="md:col-span-2"><Label className="text-sm">Date &amp; time *</Label><Input type="datetime-local" required value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" /></div>
            <div className="md:col-span-2"><Label className="text-sm">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#04060d] border-white/10 mt-1" /></div>
          </div>
          <Button type="submit" disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="svc-submit-btn">{submitting ? "Booking…" : "Book Service"}</Button>
        </form>
      </section>
    </PublicShell>
  );
}
